import { Vector3, type Camera } from 'three';
import type { WorldManager } from './WorldManager';
import { BlockType, type Vector3D, type VoxelHit, vec3 } from '../types';

const _unproject = new Vector3();

/**
 * Amanatides & Woo grid traversal — finds the first solid/interactive voxel
 * along a ray. O(range) with no allocations beyond the returned hit.
 */
export function raycastVoxels(
  world: WorldManager,
  origin: Readonly<Vector3D>,
  direction: Readonly<Vector3D>,
  maxDistance: number,
  options?: { hitPassable?: boolean },
): VoxelHit | null {
  const bs = world.blockSize;
  let dx = direction.x;
  let dy = direction.y;
  let dz = direction.z;
  const len = Math.hypot(dx, dy, dz);
  if (len < 1e-8) return null;
  dx /= len;
  dy /= len;
  dz /= len;

  let x = Math.floor(origin.x / bs);
  let y = Math.floor(origin.y / bs);
  let z = Math.floor(origin.z / bs);

  const stepX = dx >= 0 ? 1 : -1;
  const stepY = dy >= 0 ? 1 : -1;
  const stepZ = dz >= 0 ? 1 : -1;

  const tDeltaX = dx === 0 ? Infinity : Math.abs(bs / dx);
  const tDeltaY = dy === 0 ? Infinity : Math.abs(bs / dy);
  const tDeltaZ = dz === 0 ? Infinity : Math.abs(bs / dz);

  const nextBoundaryX = (dx >= 0 ? x + 1 : x) * bs;
  const nextBoundaryY = (dy >= 0 ? y + 1 : y) * bs;
  const nextBoundaryZ = (dz >= 0 ? z + 1 : z) * bs;

  let tMaxX = dx === 0 ? Infinity : Math.abs((nextBoundaryX - origin.x) / dx);
  let tMaxY = dy === 0 ? Infinity : Math.abs((nextBoundaryY - origin.y) / dy);
  let tMaxZ = dz === 0 ? Infinity : Math.abs((nextBoundaryZ - origin.z) / dz);

  let distance = 0;
  const normal = vec3();
  const hitPassable = options?.hitPassable ?? false;

  // Cap iterations to avoid infinite loops on degenerate rays.
  const maxSteps = Math.ceil(maxDistance / bs) + 3;
  for (let i = 0; i < maxSteps; i++) {
    if (distance > maxDistance) return null;

    if (world.inBounds(x, y, z)) {
      const blockType = world.getBlock(x, y, z);
      const solid = world.isSolid(x, y, z);
      const interactive =
        blockType === BlockType.ExitDoor ||
        blockType === BlockType.SurfacePortal ||
        blockType === BlockType.Shop;
      if (solid || (hitPassable && interactive) || interactive) {
        // Skip the voxel we start inside (air / door we're standing in).
        if (i > 0 || solid) {
          return {
            x,
            y,
            z,
            blockType,
            distance,
            normal: { x: normal.x, y: normal.y, z: normal.z },
          };
        }
      }
    } else if (i > 0) {
      return null;
    }

    if (tMaxX < tMaxY) {
      if (tMaxX < tMaxZ) {
        distance = tMaxX;
        x += stepX;
        tMaxX += tDeltaX;
        normal.x = -stepX;
        normal.y = 0;
        normal.z = 0;
      } else {
        distance = tMaxZ;
        z += stepZ;
        tMaxZ += tDeltaZ;
        normal.x = 0;
        normal.y = 0;
        normal.z = -stepZ;
      }
    } else if (tMaxY < tMaxZ) {
      distance = tMaxY;
      y += stepY;
      tMaxY += tDeltaY;
      normal.x = 0;
      normal.y = -stepY;
      normal.z = 0;
    } else {
      distance = tMaxZ;
      z += stepZ;
      tMaxZ += tDeltaZ;
      normal.x = 0;
      normal.y = 0;
      normal.z = -stepZ;
    }
  }

  return null;
}

/** Build a unit look direction from yaw / pitch (YXZ camera convention). */
export function lookDirectionFromAngles(yaw: number, pitch: number, out: Vector3D): Vector3D {
  const cosPitch = Math.cos(pitch);
  out.x = -Math.sin(yaw) * cosPitch;
  out.y = Math.sin(pitch);
  out.z = -Math.cos(yaw) * cosPitch;
  return out;
}

/**
 * Unit world direction from a screen tap through the active camera.
 * `clientX` / `clientY` are viewport coordinates (PointerEvent).
 */
export function screenPointToWorldDirection(
  camera: Camera,
  canvas: HTMLElement,
  clientX: number,
  clientY: number,
  out: Vector3D,
): Vector3D {
  const rect = canvas.getBoundingClientRect();
  const w = Math.max(1, rect.width);
  const h = Math.max(1, rect.height);
  const ndcX = ((clientX - rect.left) / w) * 2 - 1;
  const ndcY = -((clientY - rect.top) / h) * 2 + 1;

  _unproject.set(ndcX, ndcY, 0.5).unproject(camera);
  out.x = _unproject.x - camera.position.x;
  out.y = _unproject.y - camera.position.y;
  out.z = _unproject.z - camera.position.z;
  const len = Math.hypot(out.x, out.y, out.z);
  if (len < 1e-8) {
    lookDirectionFromAngles(0, 0, out);
    return out;
  }
  out.x /= len;
  out.y /= len;
  out.z /= len;
  return out;
}
